import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Paper,
  Slider,
  Typography,
} from '@mui/material';
import { userService } from '../../services/userService';

const TraderPreferences: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [ivThreshold, setIvThreshold] = useState(50);

  useEffect(() => {
    userService.getPreferences()
      .then((prefs) => {
        setIvThreshold((prefs.iv_naked_threshold as number) ?? 50);
      })
      .catch((e) => setError(e.message || 'Failed to load preferences'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = () => {
    setSaving(true);
    setSaved(false);
    setError('');
    userService.savePreferences({ iv_naked_threshold: ivThreshold })
      .then(() => setSaved(true))
      .catch((e) => setError(e.message || 'Failed to save preferences'))
      .finally(() => setSaving(false));
  };

  if (loading) return <Box sx={{ p: 3, textAlign: 'center' }}>Loading...</Box>;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>Trader Preferences</Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>IV Threshold for Naked Options</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          When a ticker's implied volatility is below this level, Pulse will recommend naked calls/puts
          instead of spreads. Higher IV favors spreads (defined risk, premium collection).
          Lower IV means cheaper premiums where naked options give better bang for the buck.
        </Typography>

        <Box sx={{ px: 2 }}>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
            Threshold: {ivThreshold}% IV
          </Typography>
          <Slider
            value={ivThreshold}
            onChange={(_, v) => setIvThreshold(v as number)}
            min={10}
            max={100}
            step={5}
            marks={[
              { value: 10, label: '10%' },
              { value: 25, label: '25%' },
              { value: 50, label: '50%' },
              { value: 75, label: '75%' },
              { value: 100, label: '100%' },
            ]}
            valueLabelDisplay="auto"
            valueLabelFormat={(v) => `${v}%`}
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Below {ivThreshold}% IV → Naked calls/puts
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Above {ivThreshold}% IV → Spreads (defined risk)
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Button variant="contained" onClick={handleSave} disabled={saving}>
        {saving ? 'Saving...' : 'Save Preferences'}
      </Button>

      {saved && <Alert severity="success" sx={{ mt: 2 }}>Preferences saved.</Alert>}
      {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
    </Box>
  );
};

export default TraderPreferences;
